import React from "react";
import classnames from "classnames";
import {
  ButtonColour, MortgageBlock, ButtonSize,
} from "@trussle/external-components";
import { renderRichText } from "gatsby-source-contentful/rich-text";

import { GatsbyImageOrSvg } from "../../../../../utils/GatsbyImageOrSvg/GatsbyImageOrSvg";
import { IBlock } from "../../../../../types/IBlock";
import { IContentfulBackgroundColour } from "../../../../../types/IContentfulBackgroundColour";
import { ICTA } from "../../../../../types/ICTA";
import { addTracking, CallToActions } from "../../../../CallToActions";
import { RichText } from "../../../../RichText";
import { IContentfulRichTextResponse } from "../../../../../types/IContentfulRichText";
import { transformRichText } from "../../../../../helpers/richText";
import { EVersions } from "../../../../../types/common";
import { redirect } from "../../../../../helpers/redirect";

import "./styles.scss";
import { PanelWrapper } from "../../../../PanelWrapper";

export const MAX_ALLOWED_BLOCKS = 4;

export interface IPullOutBlocksPanelOriginalProps {
  bgColour: IContentfulBackgroundColour;
  blocks: IBlock[];
  content?: IContentfulRichTextResponse;
  callToActions?: ICTA[];
  headerAnchor?: string;
  version: EVersions;
}

export const PullOutBlocksPanelOriginal: React.FC<IPullOutBlocksPanelOriginalProps> = (
  props,
) => {
  const {
    bgColour,
    blocks,
    content,
    callToActions,
    version,
    headerAnchor,
  } = props;

  const richTextContent = content ? transformRichText(content.raw) : null;

  const pullOutBlocksClasses = classnames(
    "pullOutBlocksPanel",
    {
      "pullOutBlocksPanel--versionTwo": version === EVersions.V2,
    },
  );
  const columnsClasses = ["", "", "threeColumns", "fourColumns"];

  return (
    <PanelWrapper backgroundColour={bgColour}>
      <div className={pullOutBlocksClasses} id={headerAnchor}>
        {richTextContent && (
        <div className="pullOutBlocksPanel__header">
          <RichText
            contrastText={bgColour === "navy"}
            content={richTextContent}
          />
        </div>
        )}
        <div
          className={`pullOutBlocksPanel__blockContainer ${
            blocks.length < MAX_ALLOWED_BLOCKS
              ? "threeColumns"
              : columnsClasses[MAX_ALLOWED_BLOCKS - 1]
          }`}
        >
          {blocks.map((block: IBlock, index: number) => (
            <MortgageBlock
              key={`${block.id}${index}`}
              title={block.title}
              subtitle={block.subtitle}
              description={renderRichText(block.content!)}
              image={block.image ? (
                () => (
                  <GatsbyImageOrSvg
                    loading="lazy"
                    image={block.image!}
                    alt={block.image!.title}
                  />
                )
              ) : undefined}
              classOverrides={{
                title: "mortgageBlockTitle",
                outerContainer: `mortgageBlockContainer ${getColumnClass(
                  index,
                  blocks.length,
                )}`,
                button: "mortgageBlockButton",
              }}
              cta={
                block.link
                  ? {
                    text: block.link.text,
                    onClick: () => {
                      redirect(block.link?.url || "");
                    },
                  }
                  : undefined
                }
            />
          ))}
        </div>
        {callToActions && (
        <div className="pullOutBlocksPanel__ctas">
          <CallToActions
            alignment="center"
            secondaryCtaColour={bgColour === "grey" ? ButtonColour.TertiaryLight : ButtonColour.Secondary}
            size={ButtonSize.Large}
            ctas={addTracking("Pull out blocks", ...callToActions)}
          />
        </div>
        )}
      </div>
    </PanelWrapper>

  );
};

export const getColumnClass = (index: number, totalColumns: number): string => {
  let numberOfBlocks = totalColumns;
  if (totalColumns > MAX_ALLOWED_BLOCKS) {
    numberOfBlocks = MAX_ALLOWED_BLOCKS;
  }

  const numberArray = Array.from(Array(totalColumns).keys());
  const columnClasses = numberArray.reduce((acc: string[], value: number) => {
    const columnIndex = value % numberOfBlocks;
    if (columnIndex === 0) {
      acc.push("firstColumn");
      return acc;
    }
    if (columnIndex === numberOfBlocks - 1) {
      acc.push("lastColumn");
      return acc;
    }

    acc.push("middleColumn");

    return acc;
  }, []);

  return columnClasses[index];
};
